import * as React from 'react'
import { connect } from 'react-redux'
import type { RootState, Dispatch } from '../../../app/store'
import type { BonusStatusT } from '../../../models/customer'
import Page from '../../layout/Page'
import Checkmark from '../../../icons/Checkmark'
import styles from './Stammis.module.css'
import modalStyles from '../DeliveryOptions/DeliveryOptions.module.css'
import { useNavigate, useParams } from 'react-router-dom'
import Modal from '../../Modal'

interface StammisT {
  addBonus: (payload: { id: string, storeId: string, orderId: string | null }) => void
  orderId: string | null
  bonusStatus: BonusStatusT
  createOrder: (payload?: any) => Promise<void>
  validateStammis: (payload: { ssn: string }) => Promise<void>
  addStammisPoints: (payload: { storeId: string, orderId: string }) => Promise<void>
}

function Stammis ({ orderId, bonusStatus, createOrder, validateStammis, addStammisPoints }: StammisT) {
  const { storeId } = useParams()
  const navigate = useNavigate()
  const [isStammis, setIsStammis] = React.useState('IDLE')
  const [personalNumber, setPersonalNumber] = React.useState('')
  const [validPersonalNumber, setValidPersonalNumber] = React.useState('IDLE')
  const [readyToPay, setReadyToPay] = React.useState(false)
  const [isWaitingForOrderCreation, setIsWaitingForOrderCreation] = React.useState(false)

  const isValidPersonalNumber = React.useCallback(() => {
    try {

      if (personalNumber.length !== 10) {
        setValidPersonalNumber('FALSE')
        return false
      }

      const month = personalNumber.substring(2, 4)
      const day = personalNumber.substring(4, 6)

      if (parseInt(month) > 12 || parseInt(day) > 31) {
        setValidPersonalNumber('FALSE')
        return false
      }

      const personalSum = [...new Array(9)].reduce((prev, _, index) => {
        let number = 0
        if (index % 2) {
          number = parseInt(personalNumber[index]) * 1
        } else {
          number = parseInt(personalNumber[index]) * 2
        }
        let num = number.toString().split('').reduce((prev, current) => {
          return prev + parseInt(current)
        }, 0)

        return prev + num
      }, 0)

      const nearestTen = Math.ceil(personalSum / 10) * 10;
      const controlNumber = nearestTen - personalSum;

      if (parseInt(personalNumber.charAt(9)) !== controlNumber) {
        setValidPersonalNumber('FALSE')
        return false
      }

      setValidPersonalNumber('TRUE')
      return true

    } catch (error) {
      console.log(error)
      setValidPersonalNumber('FALSE')
      return false
    }
  }, [personalNumber])

  React.useEffect(() => {
    async function handlePayment() {
      if (readyToPay === true && storeId) {
        await createOrder()
        setIsWaitingForOrderCreation(false)
      }
    }
    handlePayment()
  }, [readyToPay, storeId])

  React.useEffect(() => {
    async function processOrderAndNavigate() {
      if (storeId && orderId) {
        if (bonusStatus === 'REGISTERED') {
          await addStammisPoints({ storeId, orderId })
        }
        navigate(`/butik/${storeId}/betala/${orderId}`)
      }
    }
    processOrderAndNavigate()
  }, [orderId, storeId])

  React.useEffect(() => {
    setPersonalNumber('')
  }, [isStammis])

  React.useEffect(() => {
    if (personalNumber.length === 10) {
      isValidPersonalNumber()
    } else {
      setValidPersonalNumber('IDLE')
    }
  }, [personalNumber, isValidPersonalNumber])

  React.useEffect(() => {
    if (bonusStatus === 'REGISTERED') {
      setTimeout(() => {
        setReadyToPay(true)
      }, 2000)
    } else if (bonusStatus === 'FAILED') {
      setValidPersonalNumber('FALSE')
    }
  }, [bonusStatus])

  async function registerStammisPoints () {
    if (isValidPersonalNumber() && storeId) {
      await validateStammis({ ssn: personalNumber })
    }
  }

  async function handleStammisChoice (stammis: 'TRUE' | 'FALSE') {
    setIsStammis(stammis)
    if (stammis === 'FALSE') {
      setIsWaitingForOrderCreation(true)
      setReadyToPay(true)
    }
  }

  return (
    <Page withTopNav={false} withBackButton={true} withCancelButton={true} withCart={true}>
      {bonusStatus === 'REGISTERED' ? (
        <section className={styles.stammis}>
          <div className={styles.content}>
            <div className={styles.successContent}>
              <h3 className={styles.successHeading}>Dina stammispoäng är registrerade!</h3>
              <Checkmark className={styles.checkmark} />
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className={styles.stammis}>
            <div className={styles.content}>
              <h1 className={styles.heading}>Är du ICA Stammis?</h1>
              <div className={styles.preamble}>Som ICA Stammis kan du samla poäng på alla dina köp. <p>Är du inte stammis kan du bli det på ica.se</p></div>
              <div className={styles.buttons}>
                <button className={styles.button} type="button" onClick={() => handleStammisChoice('TRUE')}>Ja, jag är stammis</button>
                <button className={styles.button} type="button" onClick={() => handleStammisChoice('FALSE')} disabled={isWaitingForOrderCreation}>
                  {isWaitingForOrderCreation ? (
                    <span className={styles.spinner}></span>
                  ) : (
                    'Nej, gå vidare'
                  )}
                </button>
              </div>
            </div>
          </section>
          <Modal appearFrom='bottom' active={isStammis === 'TRUE'} maxWidth="600px">
            <div className={modalStyles.modalContent}>
              <>
                {validPersonalNumber === 'FALSE' && (
                  <p className={styles.errorMessage}>
                    Kontrollera det angivna numret
                  </p>
                )}
                <label>Skriv ditt personnummer, 10 siffror</label>
                <input className={`${modalStyles.valueBox} ${validPersonalNumber === 'IDLE' ? '' : validPersonalNumber === 'TRUE' ? modalStyles.validValueBox : modalStyles.errorValueBox}`} maxLength={10} type="text" pattern="/^\d+$/" value={personalNumber} onChange={(e) => setPersonalNumber(e.target.value)} placeholder='Ange personnummer'></input>
              </>
              <div className={modalStyles.modalButtonContainer}>
                <button onClick={registerStammisPoints} className={modalStyles.mainButton} type="button" form='orderDetails'>
                  Registrera
                </button>
                <button onClick={() => setIsStammis('FALSE')} className={modalStyles.lightButton} type="button" form='orderDetails'>
                  Stäng
                </button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </Page>
  )
}

function mapState (state: RootState) {
  return {
    orderId: state.order.currentOrderId,
    bonusStatus: state.customer.bonusStatus,
  }
}

function mapDispatch (dispatch: Dispatch) {
  return {
    createOrder: dispatch.order.createOrder,
    validateStammis: dispatch.customer.validateStammis,
    addStammisPoints: dispatch.customer.addStammisPoints
  }
}

export default connect(mapState, mapDispatch)(Stammis)